import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Mail, Lock, User, Phone, Shirt } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegistrationPage = () => {
  const navigate = useNavigate();
  const { user, token, login } = useContext(AuthContext);
  const [step, setStep] = useState(user ? 2 : 1);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Step 1: Auth
  const [isLogin, setIsLogin] = useState(true);
  const [authData, setAuthData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: ''
  });

  // Step 2: Registration
  const [registrationData, setRegistrationData] = useState({
    captain: '',
    level: '',
    with_shirt: false,
    shirt_size: ''
  });

  const captains = [
    { name: 'Denilson', team: 'Time Verde', color: '#C5FF00' },
    { name: 'Roselma', team: 'Time Preto', color: '#1A1A1A' },
    { name: 'Rilary', team: 'Time Branco', color: '#FFFFFF' }
  ];

  const levels = ['Iniciante', 'Intermediário', 'Avançado'];

  const shirtSizes = ['P', 'M', 'G', 'GG', 'XG'];

  // Fetch registration stats
  React.useEffect(() => {
    if (step === 2) {
      fetchStats();
    }
  }, [step]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/registration-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: authData.email, password: authData.password }
        : authData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      login(response.data.access_token, response.data.user);
      toast.success(isLogin ? 'Login realizado!' : 'Conta criada com sucesso!');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar requisição');
    } finally {
      setLoading(false);
    }
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    
    if (!registrationData.captain) {
      toast.error('Selecione um capitão');
      return;
    }

    if (!registrationData.level) {
      toast.error('Selecione seu nível');
      return;
    }

    if (registrationData.with_shirt && !registrationData.shirt_size) {
      toast.error('Selecione o tamanho da camisa');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/registrations`,
        registrationData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      toast.success('Inscrição realizada! Prossiga para o pagamento.');
      navigate('/pagamento');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao realizar inscrição');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 text-muted-foreground hover:text-white font-mono uppercase text-xs"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="font-unbounded font-bold text-4xl md:text-5xl tracking-tight uppercase">
            <span className="text-white">Inscrição</span>
            <br />
            <span className="text-primary">Challenge Run 6.0</span>
          </h1>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center gap-4 mb-12">
          <div className={`flex-1 h-1 transition-colors ${
            step >= 1 ? 'bg-primary' : 'bg-white/10'
          }`} />
          <div className={`flex-1 h-1 transition-colors ${
            step >= 2 ? 'bg-primary' : 'bg-white/10'
          }`} />
        </div>

        {/* Step 1: Authentication */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-8"
          >
            <div className="mb-8">
              <h2 className="font-unbounded font-bold text-2xl mb-2">
                {isLogin ? 'Fazer Login' : 'Criar Conta'}
              </h2>
              <p className="text-muted-foreground">
                {isLogin ? 'Entre com sua conta' : 'Cadastre-se para continuar'}
              </p>
            </div>

            <form onSubmit={handleAuth} className="space-y-6">
              <div>
                <Label htmlFor="email" className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    required
                    value={authData.email}
                    onChange={(e) => setAuthData({ ...authData, email: e.target.value })}
                    className="pl-12 bg-secondary/50 border-white/10 focus:border-primary focus:ring-1 focus:ring-primary/50 font-mono text-sm h-12"
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              {!isLogin && (
                <>
                  <div>
                    <Label htmlFor="full_name" className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                      Nome Completo
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="full_name"
                        data-testid="fullname-input"
                        type="text"
                        required
                        value={authData.full_name}
                        onChange={(e) => setAuthData({ ...authData, full_name: e.target.value })}
                        className="pl-12 bg-secondary/50 border-white/10 focus:border-primary focus:ring-1 focus:ring-primary/50 font-mono text-sm h-12"
                        placeholder="Seu nome"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone" className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                      Telefone
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="phone"
                        data-testid="phone-input"
                        type="tel"
                        required
                        value={authData.phone}
                        onChange={(e) => setAuthData({ ...authData, phone: e.target.value })}
                        className="pl-12 bg-secondary/50 border-white/10 focus:border-primary focus:ring-1 focus:ring-primary/50 font-mono text-sm h-12"
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="password" className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                  Senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    required
                    value={authData.password}
                    onChange={(e) => setAuthData({ ...authData, password: e.target.value })}
                    className="pl-12 bg-secondary/50 border-white/10 focus:border-primary focus:ring-1 focus:ring-primary/50 font-mono text-sm h-12"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <Button
                data-testid="auth-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-black font-unbounded font-bold uppercase tracking-wider hover:bg-primary/90 h-12 skew-btn"
              >
                <span>{loading ? 'Processando...' : (isLogin ? 'Entrar' : 'Criar Conta')}</span>
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  {isLogin ? 'Não tem conta? Cadastre-se' : 'Já tem conta? Faça login'}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Step 2: Registration Details */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-8"
          >
            <div className="mb-8">
              <h2 className="font-unbounded font-bold text-2xl mb-2">
                Detalhes da Inscrição
              </h2>
              <p className="text-muted-foreground">
                Complete suas informações
              </p>
            </div>

            <form onSubmit={handleRegistration} className="space-y-8">
              {/* Captain Selection */}
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-4 block">
                  Escolha seu capitão e time
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {captains.map((captain) => {
                    const available = stats?.captains?.[captain.name]?.available || 14;
                    const isAvailable = available > 0;
                    return (
                      <button
                        key={captain.name}
                        data-testid={`captain-${captain.name.toLowerCase()}-button`}
                        type="button"
                        onClick={() => isAvailable && setRegistrationData({ ...registrationData, captain: captain.name })}
                        disabled={!isAvailable}
                        className={`p-6 border transition-all duration-200 ${
                          !isAvailable
                            ? 'opacity-50 cursor-not-allowed border-white/10'
                            : registrationData.captain === captain.name
                            ? 'border-primary bg-primary/10'
                            : 'border-white/10 hover:border-primary/50'
                        }`}
                      >
                        <div
                          className="w-20 h-20 rounded-full mx-auto mb-4"
                          style={{ 
                            backgroundColor: captain.color,
                            boxShadow: registrationData.captain === captain.name ? `0 0 20px ${captain.color}60` : 'none',
                            border: captain.team === 'Time Branco' ? '2px solid rgba(197, 255, 0, 0.3)' : 'none'
                          }}
                        />
                        <p className="font-unbounded font-bold text-lg mb-1">{captain.name}</p>
                        <p className="text-muted-foreground text-sm">{captain.team}</p>
                        <p className={`text-xs mt-2 font-bold ${isAvailable ? 'text-primary' : 'text-red-500'}`}>
                          {isAvailable ? `${available} vagas restantes` : 'Esgotado'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Level Selection */}
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-4 block">
                  Escolha seu nível
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {levels.map((level) => {
                    const available = stats?.levels?.[level]?.available || 14;
                    const isAvailable = available > 0;
                    return (
                      <button
                        key={level}
                        data-testid={`level-${level.toLowerCase()}-button`}
                        type="button"
                        onClick={() => isAvailable && setRegistrationData({ ...registrationData, level })}
                        disabled={!isAvailable}
                        className={`p-6 border transition-all duration-200 ${
                          !isAvailable
                            ? 'opacity-50 cursor-not-allowed border-white/10'
                            : registrationData.level === level
                            ? 'border-primary bg-primary/10'
                            : 'border-white/10 hover:border-primary/50'
                        }`}
                      >
                        <p className="font-unbounded font-bold text-xl mb-2">{level}</p>
                        <p className={`text-xs font-bold ${isAvailable ? 'text-primary' : 'text-red-500'}`}>
                          {isAvailable ? `${available} vagas restantes` : 'Esgotado'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Shirt Option */}
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-4 block">
                  Incluir camisa oficial?
                </Label>
                <RadioGroup
                  value={registrationData.with_shirt.toString()}
                  onValueChange={(value) => setRegistrationData({ 
                    ...registrationData, 
                    with_shirt: value === 'true',
                    shirt_size: value === 'false' ? '' : registrationData.shirt_size
                  })}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div 
                      data-testid="without-shirt-option"
                      className={`border p-6 cursor-pointer transition-all ${
                        !registrationData.with_shirt ? 'border-primary bg-primary/10' : 'border-white/10 hover:border-primary/50'
                      }`}
                      onClick={() => setRegistrationData({ ...registrationData, with_shirt: false, shirt_size: '' })}
                    >
                      <RadioGroupItem value="false" id="without-shirt" className="sr-only" />
                      <Label htmlFor="without-shirt" className="cursor-pointer">
                        <div className="font-unbounded font-bold text-xl mb-2">Sem Camisa</div>
                        <div className="text-3xl font-bold text-primary mb-2">R$ 65</div>
                        <div className="text-muted-foreground text-sm">Apenas inscrição</div>
                      </Label>
                    </div>

                    <div 
                      data-testid="with-shirt-option"
                      className={`border p-6 cursor-pointer transition-all ${
                        registrationData.with_shirt ? 'border-primary bg-primary/10' : 'border-white/10 hover:border-primary/50'
                      }`}
                      onClick={() => setRegistrationData({ ...registrationData, with_shirt: true })}
                    >
                      <RadioGroupItem value="true" id="with-shirt" className="sr-only" />
                      <Label htmlFor="with-shirt" className="cursor-pointer">
                        <div className="font-unbounded font-bold text-xl mb-2">Com Camisa</div>
                        <div className="text-3xl font-bold text-primary mb-2">R$ 100</div>
                        <div className="text-muted-foreground text-sm">Inscrição + camisa</div>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Shirt Size */}
              {registrationData.with_shirt && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <Label htmlFor="shirt-size" className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-4 block">
                    Tamanho da Camisa
                  </Label>
                  <Select 
                    value={registrationData.shirt_size}
                    onValueChange={(value) => setRegistrationData({ ...registrationData, shirt_size: value })}
                  >
                    <SelectTrigger 
                      id="shirt-size"
                      data-testid="shirt-size-select"
                      className="bg-secondary/50 border-white/10 focus:border-primary h-12"
                    >
                      <SelectValue placeholder="Selecione o tamanho" />
                    </SelectTrigger>
                    <SelectContent>
                      {shirtSizes.map((size) => (
                        <SelectItem key={size} value={size}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </motion.div>
              )}

              <Button
                data-testid="registration-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-black font-unbounded font-bold uppercase tracking-wider hover:bg-primary/90 h-12 skew-btn"
              >
                <span>{loading ? 'Processando...' : 'Prosseguir para Pagamento'}</span>
              </Button>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default RegistrationPage;
